import type { DailyHours, AttendanceRow } from '../../services/p9315Api';

/**
 * Build a map from day number (1-31) to hours for quick column rendering.
 * Avoids O(n) lookup per cell in the table.
 */
export function buildDayHoursMap(dailyHours: DailyHours[] | null | undefined): Record<number, number> {
  const map: Record<number, number> = {};
  if (!dailyHours) return map;
  for (const dh of dailyHours) {
    map[dh.day] = dh.hours;
  }
  return map;
}

/**
 * Get Chinese weekday label for a specific date.
 * Returns one of: 日, 一, 二, 三, 四, 五, 六
 */
const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六'];

export function getWeekdayLabel(year: number, month: number, day: number): string {
  if (!year || !month || !day) return '';
  const date = new Date(year, month - 1, day);
  return WEEKDAY_LABELS[date.getDay()];
}

/**
 * Build print HTML for the attendance time report.
 * Generates a pivot table with employee rows and day columns.
 */
export function buildPrintHtml(
  data: AttendanceRow[],
  year: number,
  month: number,
  daysInMonth: number,
): string {
  // Build day headers
  const dayHeaders = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const wd = getWeekdayLabel(year, month, d);
    const isWeekend = wd === '六' || wd === '日';
    dayHeaders.push(
      `<th style="${isWeekend ? 'color:#ff4d4f' : ''}">${d}<br/><span style="font-size:9px">${wd}</span></th>`
    );
  }

  // Build data rows
  const rows = data.map(r => {
    const map = buildDayHoursMap(r.dailyHours);
    const dayCells = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const h = map[d];
      dayCells.push(`<td class="right">${h && h > 0 ? h.toFixed(1) : ''}</td>`);
    }
    return `<tr>
<td>${r.F910_18 || ''}</td><td>${r.dept_name || ''}</td>
<td>${r.F910_01}</td><td>${r.F901_11 || ''}</td>
${dayCells.join('')}
<td class="right" style="font-weight:bold">${r.F910_31 != null ? r.F910_31.toFixed(1) : ''}</td>
<td class="right">${r.F910_32 ?? ''}</td>
</tr>`;
  });

  return `<!DOCTYPE html><html><head><title>部門個人出勤時間</title>
<style>
body{font-family:'Microsoft JhengHei',sans-serif;margin:10px;font-size:10px}
h2{text-align:center;font-size:14px}
.info{text-align:center;margin-bottom:8px;font-size:11px}
table{width:100%;border-collapse:collapse}
th,td{border:1px solid #333;padding:1px 3px}
th{background:#f0f0f0;text-align:center;font-size:9px}
.right{text-align:right}
@media print{body{margin:5mm}@page{size:A4 landscape}}
</style></head><body>
<h2>部門個人出勤時間</h2>
<div class="info">${year}年 ${String(month).padStart(2, '0')}月</div>
<table><thead><tr>
<th>部門</th><th>部門名稱</th><th>員工代號</th><th>姓名</th>
${dayHeaders.join('')}
<th>合計<br/>時數</th><th>出勤<br/>天數</th>
</tr></thead><tbody>
${rows.join('')}
</tbody></table></body></html>`;
}
